import { differenceInDays, formatDistance } from "date-fns";
import { DateInterval } from "../../enums/date-interval.enum";
import { DateIntervalFilterData } from "../../models/filters/widget-filter-data.model";
import { enUS, es, fr } from 'date-fns/locale';

export type KpiTranslate = (key: string, params?: Record<string, unknown>) => string;

function label(translate: KpiTranslate | undefined, key: string, fallback: string, params?: Record<string, unknown>): string {
  return translate ? translate(`kpi.periods.${key}`, params) : fallback;
}

function duration(startDate: Date, endDate: Date, language: string): string {
  const locale = language.startsWith('es') ? es : language.startsWith('fr') ? fr : enUS;
  return formatDistance(startDate, endDate, { locale });
}

export function getSelectedFilterLabel(filter: DateIntervalFilterData, translate?: KpiTranslate, language = 'en'): string {
  if (!filter) return '';

  if (filter.interval === DateInterval.Day && !filter.isCustomDate) return label(translate, 'previousDay', 'previous day');
  if (filter.interval === DateInterval.Week && !filter.isCustomDate) return label(translate, 'previousWeek', 'previous week');
  if (filter.interval === DateInterval.Month && !filter.isCustomDate) return label(translate, 'lastMonth', 'last month');
  if (filter.isCustomDate) return getCustomDateFilterLabel(filter, translate, language);

  return label(translate, 'lastSixMonths', 'last 6 months');
}

function getCustomDateFilterLabel(filter: DateIntervalFilterData, translate?: KpiTranslate, language = 'en'): string {
  const startDate = filter.startDate ?? new Date();
  const endDate = filter.endDate ?? new Date();
  const dateDiff = differenceInDays(endDate, startDate) + 1;

  return dateDiff > 29
    ? label(translate, 'previousDays', `previous ${dateDiff} days`, { count: dateDiff })
    : label(translate, 'previousDuration', `previous ${formatDistance(startDate, endDate)}`, { duration: duration(startDate, endDate, language) });
}

export function getModalFilterHeaders(filter: DateIntervalFilterData, translate?: KpiTranslate, language = 'en'): { previous: string; current: string } {
  const startDate = filter.startDate ?? new Date();
  const endDate = filter.endDate ?? new Date();
  const previousStartDate = filter.previousCycleStartDate ?? new Date();
  const previousEndDate = filter.previousCycleEndDate ?? new Date();
  const dateDiff = differenceInDays(endDate, startDate) + 1;

  if (filter.interval === DateInterval.Day) {
    return { previous: label(translate, 'yesterday', 'Yesterday'), current: label(translate, 'today', 'Today') };
  }

  if (filter.interval === DateInterval.Week) {
    if (formatDistance(startDate!, endDate!) === '7 days' && !filter.isCustomDate) return { previous: label(translate, 'lastWeek', 'Last Week'), current: label(translate, 'thisWeek', 'This Week') };
    if (formatDistance(startDate!, endDate!) === '14 days') return { previous: label(translate, 'lastTwoWeeks', 'Last 2 Weeks'), current: label(translate, 'thisTwoWeeks', 'This 2 Weeks') };
    if (formatDistance(startDate!, endDate!) === '21 days') return { previous: label(translate, 'lastThreeWeeks', 'Last 3 Weeks'), current: label(translate, 'thisThreeWeeks', 'This 3 Weeks') };
    if (formatDistance(startDate!, endDate!) === 'about 1 month' && !filter.isCustomDate) return { previous: label(translate, 'lastMonthHeading', 'Last Month'), current: label(translate, 'thisMonth', 'This Month') };

    // Using previousCycleStartDate and previousCycleEndDate to represent 1 week filter due to possibility of current interval dateDiff not being 7 days
    if (formatDistance(previousEndDate!, previousStartDate!) === '7 days' && !filter.isCustomDate) return { previous: label(translate, 'lastWeek', 'Last Week'), current: label(translate, 'thisWeek', 'This Week') };

    return {
      previous: getCustomDateFilterLabel(filter, translate, language),
      current: label(translate, 'current', 'Current'),
    };
  }

  if (filter.interval === DateInterval.Month) {
    return { previous: label(translate, 'lastMonthHeading', 'Last Month'), current: label(translate, 'thisMonth', 'This Month') };
  }

  return {
    previous: label(translate, 'previousDurationHeading', `Previous ${formatDistance(endDate!, startDate!)}`, { duration: duration(endDate, startDate, language) }),
    current: label(translate, 'currentDuration', `Current ${formatDistance(endDate!, startDate!)}`, { duration: duration(endDate, startDate, language) }),
  };
}